import { useEffect, useState } from 'react';
import '../../styles/login.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useUI } from '../../shared/UI/Uicontext';
import { alertService } from '../../shared/services/alertService';
import { useLoginMutation } from './Login.service';
import { decryptData } from '../../shared/services/cryptoService';
import { getSessionData } from 'utils.session';
import { toast } from 'react-toastify';
import { ENV_CONFIG } from '../../shared/config/env.config';
import { AppDispatch } from '../../store';
import { loadSession } from '../../store/slices/sessionSlice';
import { decodeSessionFromStorage } from '../../shared/services/userSessionService';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showLoader, hideLoader } = useUI();

  // ================= LOCKOUT HELPERS =================
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION_MS = 30 * 60 * 1000; // 30 minutes

  const getAttemptData = () => {
    const raw = localStorage.getItem('loginAttempts');
    if (!raw) return { count: 0, lockedUntil: null };
    try {
      return JSON.parse(raw);
    } catch {
      return { count: 0, lockedUntil: null };
    }
  };

  const setAttemptData = (data: {
    count: number;
    lockedUntil: number | null;
  }) => {
    localStorage.setItem('loginAttempts', JSON.stringify(data));
  };

  const isCurrentlyLocked = () => {
    const { lockedUntil } = getAttemptData();
    // eslint-disable-next-line react-hooks/purity
    if (lockedUntil && Date.now() < lockedUntil) {
      return true;
    }
    // lock expired — reset
    // eslint-disable-next-line react-hooks/purity
    if (lockedUntil && Date.now() >= lockedUntil) {
      setAttemptData({ count: 0, lockedUntil: null });
    }
    return false;
  };

  const getRemainingLockTime = () => {
    const { lockedUntil } = getAttemptData();
    if (!lockedUntil) return '';

    // eslint-disable-next-line react-hooks/purity
    const diffMs = lockedUntil - Date.now();
    const totalMinutes = Math.ceil(diffMs / (60 * 1000));

    if (totalMinutes <= 1) {
      return 'a minute';
    }
    return `${totalMinutes} minutes`;
  };

  const [isLocked, setIsLocked] = useState(() => isCurrentlyLocked());

  // In Login.tsx — update useEffect
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const session = getSessionData();
      const uType = Number(session?.usertypeid) || 0;
      const uType1 = Number(session?.emisUsertype1) || 0;
      const storedUsername = localStorage.getItem('username');
      // HR users (usertype=21, usertype1=17) or admin user go to mail-request
      if ((uType === 21 && uType1 === 23) || storedUsername === '4028619') {
        navigate('/mail-request', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (!isLocked) return;
    const interval = setInterval(() => {
      if (!isCurrentlyLocked()) {
        setIsLocked(false);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isLocked]);
  const [confirmPassword, setConfirmPassword] = useState('');
  // ================= LOGIN =================
  const [passShow, setPassShow] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ================= FORGOT PASSWORD =================
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotTouched, setForgotTouched] = useState(false);
  const [forgetSubmitted, setForgetSubmitted] = useState(false);

  const [otpValue, setOtpValue] = useState('');
  const [showPass, setShowPass] = useState(false);

  const [newPassValue, setNewPassValue] = useState('');
  const [validation, setValidation] = useState(true);

  const usernameError = !username && (submitted || usernameTouched);

  const passwordError = !password && (submitted || passwordTouched);

  const forgotError = !forgotUsername && (forgetSubmitted || forgotTouched);
  // ================= LOGIN =================
  const [loginApi] = useLoginMutation();

  const handleLogin = async () => {
    //console.log(ENV_CONFIG, 'ENV_CONFIG');

    setSubmitted(true);
    if (!username || !password) return;

    if (isCurrentlyLocked()) {
      setIsLocked(true);
      const timeLeft = getRemainingLockTime();
      alertService.error(
        `Your account is temporarily locked due to multiple incorrect login attempts. Please try again in ${timeLeft}.`
      );
      return;
    }

    try {
      showLoader();

      const loginResponse = await loginApi({ username, password }).unwrap();

      let decryptedResponse;
      try {
        decryptedResponse = decryptData(loginResponse);
      } catch {
        decryptedResponse = loginResponse;
      }

      if (!decryptedResponse?.dataStatus) {
        hideLoader();

        // ── Track failed attempt ──
        const { count } = getAttemptData();
        const newCount = count + 1;

        if (newCount >= MAX_ATTEMPTS) {
          setAttemptData({
            count: newCount,
            // eslint-disable-next-line react-hooks/purity
            lockedUntil: Date.now() + LOCKOUT_DURATION_MS,
          });
          alertService.error(
            'Too many incorrect login attempts. Your account has been temporarily locked for 30 minutes. Please try again later.'
          );
        } else {
          setAttemptData({ count: newCount, lockedUntil: null });
          alertService.error(
            decryptedResponse?.message || 'Invalid username or password'
          );
        }
        return;
      }

      // ── Login succeeded — reset attempt tracking ──
      setAttemptData({ count: 0, lockedUntil: null });

      const token = decryptedResponse?.records?.token;
      if (!token) {
        alertService.error('Token not found');
        return;
      }

      localStorage.setItem('token', token);

      const sessionData = getSessionData();
      //console.log('Decoded Session Data:', sessionData);
      //console.log('mod2:', sessionData?.mod2);
      //console.log('usertypeid:', sessionData?.usertypeid);
      //console.log('usertypeid:', sessionData?.emisUsertype1);

      const blockedUserTypes = [1, 8, 11, 22, 28];
      const isBlockedTeacher =
        sessionData?.usertypeid === 14 && sessionData?.teacherType !== 103;

      if (
        blockedUserTypes.includes(sessionData?.usertypeid) ||
        isBlockedTeacher
      ) {
        alertService.info('Please Login from "emis.tnschools.gov.in"');
        localStorage.clear();
        return;
      }

      localStorage.setItem('token', token);
      localStorage.setItem('username', username);

      // Hydrate the Redux session store from the stored token so the app has
      // the session immediately (App only hydrates on mount / full reload).
      const session = decodeSessionFromStorage();
      if (session) dispatch(loadSession(session));

      hideLoader();
      alertService.success('Login Successful');

      navigate('/dashboard');
    } catch (error: any) {
      hideLoader();
      const message =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        'Login Failed';
      toast.dismiss();
      alertService.error(message);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Brand */}
        <div className="login-card__brand">
          <span className="login-card__logo">
            <i className="pi pi-graduation-cap" />
          </span>
          <div className="login-card__brand-text">
            <h1 className="login-card__title">EMIS Dashboard</h1>
            <p className="login-card__subtitle">School Education Department</p>
            <p className="login-card__subtitle">Government of Tamil Nadu</p>
          </div>
        </div>

        <form
          className="login-card__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          {/* Username */}
          <div className="login-field">
            <label htmlFor="loginUsername" className="login-field__label">
              USERNAME
            </label>
            <div className="login-field__control">
              <i className="pi pi-user login-field__icon" />
              <input
                type="text"
                id="loginUsername"
                className="login-field__input"
                placeholder="Enter username"
                value={username}
                maxLength={8}
                onChange={(e) =>
                  setUsername(e.target.value.replace(/[^0-9]/g, ''))
                }
                onBlur={() => setUsernameTouched(true)}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>
            {usernameError && (
              <small className="login-field__error">Enter User Name</small>
            )}
          </div>

          {/* Password */}
          <div className="login-field">
            <label htmlFor="loginPassword" className="login-field__label">
              PASSWORD
            </label>
            <div className="login-field__control">
              <i className="pi pi-lock login-field__icon" />
              <input
                type={passShow ? 'password' : 'text'}
                id="loginPassword"
                className="login-field__input"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
              />
              <button
                type="button"
                className="login-field__toggle"
                aria-label={passShow ? 'Show password' : 'Hide password'}
                onClick={() => setPassShow(!passShow)}
              >
                <i className={passShow ? 'pi pi-eye' : 'pi pi-eye-slash'} />
              </button>
            </div>
            {passwordError && (
              <small className="login-field__error">Enter Password</small>
            )}
          </div>

          <button type="submit" className="login-card__submit">
            <i className="pi pi-sign-in" />
            Sign In
          </button>
        </form>

        {/* <p className="login-card__note">
          Demo dashboard. All figures are generated sample data, not official
          statistics.
        </p> */}
      </div>
    </div>
  );
}
